import {
  BriefcaseIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import React, { useMemo } from 'react';

import { FirmLink } from '@/components/entity-links/entities/FirmLink';
import { enhancedApi } from '@/services/api/generated/firmsService';

import { BentoCard } from './BentoCard';

const { useFirmsListQuery } = enhancedApi;

export const ManagingParalegalFirmsPanel: React.FC<{
  userId: string;
}> = ({ userId }) => {
  const { data: firms, isLoading } = useFirmsListQuery();

  const managedFirms = useMemo(() => {
    return firms?.filter((firm) => firm.managing_paralegal_id === userId) || [];
  }, [firms, userId]);

  // If the user is not a managing paralegal for any firm, don't show this panel
  if (isLoading || managedFirms.length === 0) {
    return null;
  }

  return (
    <BentoCard>
      <div className="flex flex-col gap-4">
        {/* Header with emphasis */}
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-zinc-100 text-zinc-500">
            <BriefcaseIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 pt-0.5">
            <h3 className="text-sm font-bold text-zinc-900 leading-tight">
              Managing Paralegal
            </h3>
            <p className="mt-0.5 text-xs text-zinc-500">
              to{' '}
              <span className="font-semibold text-zinc-900">
                {managedFirms.length}
              </span>{' '}
              {managedFirms.length === 1 ? 'Firm' : 'Firms'}
            </p>
          </div>
        </div>

        {/* Firm List */}
        <div className="flex flex-col gap-2">
          {managedFirms.map((firm) => (
            <div
              key={firm.id}
              className="group flex items-center gap-3 rounded-xl bg-zinc-50 p-3 transition-all hover:bg-zinc-100 hover:shadow-sm"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-zinc-200 text-zinc-400 transition-colors group-hover:text-zinc-600">
                <BuildingOffice2Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <FirmLink entityId={firm.id}>
                  <p className="truncate text-sm font-semibold text-zinc-900 group-hover:text-blue-600 transition-colors">
                    {firm.name}
                  </p>
                </FirmLink>
                {firm.primary_state && (
                  <p className="truncate text-xs text-zinc-500">
                    {firm.primary_state}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </BentoCard>
  );
};
